import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import InteractiveMap from '../components/InteractiveMap';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useBattleMode } from '@/contexts/BattleModeContext';
import { BACKEND_URL } from '@/API_CONFIG';

const API = BACKEND_URL;

const Battle = ({ user, onBack, onUpdateUser, onOpenStore }) => {
  const { enableBattleMode, disableBattleMode } = useBattleMode();
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const interactiveMapRef = useRef(null);

  // 🎮 Activate Battle Mode on mount, deactivate on unmount
  useEffect(() => {
    enableBattleMode();
    
    return () => {
      disableBattleMode();
    };
  }, [enableBattleMode, disableBattleMode]);

  // Disable context menu (long-press menu)
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingBackground(true);

    try {
      // Convert to Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;

        // Upload to backend
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/api/profile/upload-battle-background`,
          { profile_picture: base64Image },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Battle background updated!');
        await onUpdateUser();
        setUploadingBackground(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading battle background:', error);
      toast.error('Failed to upload battle background');
      setUploadingBackground(false);
    }
  };

  const handleRemoveBackground = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/profile/battle-background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Battle background removed!');
      await onUpdateUser();
      setShowCustomizeModal(false);
    } catch (error) {
      console.error('Error removing battle background:', error);
      toast.error('Failed to remove battle background');
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0 text-white p-4 overflow-hidden select-none"
        onContextMenu={handleContextMenu}
        style={{
          backgroundImage: user.battle_stadium_background ? `url(${user.battle_stadium_background})` : 'url(https://customer-assets.emergentagent.com/job_0ddfa8dd-f7f0-44b2-a1dd-341ae5dc7366/artifacts/6tz3u9fb_IMG_1461.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          overscrollBehavior: 'none'
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        
        {/* Header - Fixed at top with highest z-index */}
        <div className="fixed top-0 left-0 right-0 z-[60] p-4 bg-gradient-to-b from-slate-900/95 to-transparent">
          <div className="flex items-center justify-center relative">
            <button
              onClick={onBack}
              className="absolute left-0 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 
              className="text-2xl font-bold text-red-400 digital-font text-center drop-shadow-lg"
            >
              BATTLE ARENA
            </h1>
            <button
              onClick={() => setShowCustomizeModal(true)}
              className="absolute right-0 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-semibold"
            >
              Customize
            </button>
          </div>
        </div>

        {/* Content wrapper - fills remaining space */}
        <div className="relative z-10 h-full pt-8 overflow-hidden">
          {/* Interactive Battle Stadium */}
          <div className="h-full">
            <InteractiveMap 
              ref={interactiveMapRef}
              user={user} 
              nearbyPlayers={[]}
              onPlayerClick={() => {}}
              onOpenStore={onOpenStore}
            />
          </div>
        </div>
      </div>

      {/* Customize Background Modal */}
      <Dialog open={showCustomizeModal} onOpenChange={setShowCustomizeModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">BATTLE STADIUM BACKGROUND</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize your Battle Stadium with a background image
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Preview - Always show current background (custom or default) */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Current Background {!user.battle_stadium_background && <span className="text-cyan-400">(Default)</span>}</label>
              <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-cyan-500/50">
                <img
                  src={user.battle_stadium_background || 'https://customer-assets.emergentagent.com/job_0ddfa8dd-f7f0-44b2-a1dd-341ae5dc7366/artifacts/6tz3u9fb_IMG_1461.jpeg'}
                  alt="Battle Stadium background"
                  className="w-full h-full object-cover"
                />
                {!user.battle_stadium_background && (
                  <div className="absolute bottom-2 right-2 bg-cyan-500/80 text-white text-xs px-2 py-1 rounded">
                    Default
                  </div>
                )}
              </div>
            </div>

            {/* Upload Button */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.battle_stadium_background ? 'Change Background' : 'Upload Custom Background'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleBackgroundUpload}
                disabled={uploadingBackground}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: JPG, PNG, or WEBP. Max size: 5MB
              </p>
            </div>

            {/* Remove Button - Only show if user has custom background */}
            {user.battle_stadium_background && (
              <Button
                onClick={handleRemoveBackground}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Reset to Default
              </Button>
            )}

            {uploadingBackground && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowCustomizeModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Battle;
